import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
} from '@mui/material';

interface RenameResponse {
  IsSuccess: boolean;
  ErrorMessage?: string;
}
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useCompanyStore } from '@/stores/company-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useSnackbarStore } from '@certara/common-react-components';

type RenameStrategy = 'prefix' | 'suffix' | 'replace';

interface BatchRenameItem {
  originalName: string;
  newName: string;
}

interface BatchRenameDragProps {
  open: boolean;
  items: string[]; // Array of original names
  onClose: () => void;
}

export function BatchRenameDialog({ open, items = [], onClose }: BatchRenameDragProps) {
  const [strategy, setStrategy] = useState<RenameStrategy>('prefix');
  const [textValue, setTextValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [replaceValue, setReplaceValue] = useState('');
  const [error, setError] = useState('');
  const [previewItems, setPreviewItems] = useState<BatchRenameItem[]>([]);

  const { activeCompany } = useCompanyStore();
  const { currentPath } = useNavigationStore();
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  // Calculate preview names
  useEffect(() => {
    const calculated: BatchRenameItem[] = items.map((originalName) => {
      let newName = originalName;

      switch (strategy) {
        case 'prefix':
          newName = textValue ? `${textValue}${originalName}` : originalName;
          break;
        case 'suffix':
          newName = textValue ? `${originalName}${textValue}` : originalName;
          break;
        case 'replace':
          newName = originalName.replace(new RegExp(searchValue, 'g'), replaceValue);
          break;
      }

      return { originalName, newName };
    });

    setPreviewItems(calculated);
  }, [strategy, textValue, searchValue, replaceValue, items]);

  const renameMutation = useMutation({
    mutationFn: async () => {
      if (!activeCompany) throw new Error('Company not selected');

      // Call rename API for each item
      const results = await Promise.allSettled(
        previewItems.map((item) =>
          apiClient.postForm<RenameResponse>('api/Storage/RenameFile', {
            companyId: activeCompany.Id,
            path: currentPath || '/',
            oldFileName: item.originalName,
            newFileName: item.newName,
          })
        )
      );

      return results;
    },
    onSuccess: (results) => {
      const successful = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - successful;

      if (failed > 0) {
        addSnackbar(`Renamed ${successful} of ${results.length} items`, 'warning');
      } else {
        addSnackbar(`Successfully renamed ${successful} items`, 'success');
      }

      queryClient.invalidateQueries({
        queryKey: ['gridData', activeCompany?.Id, currentPath],
      });
      handleClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      addSnackbar('Batch rename failed', 'error', message, true);
    },
  });

  const handleClose = () => {
    setStrategy('prefix');
    setTextValue('');
    setSearchValue('');
    setReplaceValue('');
    setError('');
    onClose();
  };

  const handleRename = () => {
    setError('');

    // Validation
    if (strategy !== 'replace' && !textValue.trim()) {
      setError('Please enter a value');
      return;
    }

    if (strategy === 'replace' && !searchValue.trim()) {
      setError('Please enter a search value');
      return;
    }

    // Check if any names would change
    const hasChanges = previewItems.some((item) => item.newName !== item.originalName);
    if (!hasChanges) {
      setError('No changes to apply');
      return;
    }

    renameMutation.mutate();
  };

  const isProcessing = renameMutation.isPending;
  const hasChanges = previewItems.some((item) => item.newName !== item.originalName);

  return (
    <Dialog
      data-testid="batch-rename-dialog"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minWidth: '500px' },
      }}
    >
      <DialogTitle data-testid="batch-rename-dialog-title">
        Rename Multiple Files ({items.length} selected)
      </DialogTitle>

      {isProcessing && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
          }}
        />
      )}

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Strategy Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Rename Strategy
            </Typography>
            <RadioGroup
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as RenameStrategy)}
              data-testid="batch-rename-strategy-group"
            >
              <FormControlLabel
                value="prefix"
                control={
                  <Radio size="small" data-testid="batch-rename-strategy-prefix" />
                }
                label="Add prefix"
                disabled={isProcessing}
              />
              <FormControlLabel
                value="suffix"
                control={
                  <Radio size="small" data-testid="batch-rename-strategy-suffix" />
                }
                label="Add suffix"
                disabled={isProcessing}
              />
              <FormControlLabel
                value="replace"
                control={
                  <Radio size="small" data-testid="batch-rename-strategy-replace" />
                }
                label="Find and replace"
                disabled={isProcessing}
              />
            </RadioGroup>
          </Box>

          {/* Input Fields */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {strategy === 'replace' ? 'Find & Replace' : 'Text Value'}
            </Typography>

            {strategy === 'replace' ? (
              <Stack spacing={1}>
                <TextField
                  data-testid="batch-rename-find-input"
                  fullWidth
                  size="small"
                  label="Find (regex supported)"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  disabled={isProcessing}
                />
                <TextField
                  data-testid="batch-rename-replace-input"
                  fullWidth
                  size="small"
                  label="Replace with"
                  value={replaceValue}
                  onChange={(e) => setReplaceValue(e.target.value)}
                  disabled={isProcessing}
                />
              </Stack>
            ) : (
              <TextField
                data-testid="batch-rename-text-input"
                fullWidth
                size="small"
                label={strategy === 'prefix' ? 'Prefix' : 'Suffix'}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                disabled={isProcessing}
                placeholder={strategy === 'prefix' ? 'e.g., backup_' : 'e.g., _old'}
              />
            )}

            {error && (
              <Typography
                color="error"
                variant="caption"
                sx={{ display: 'block', mt: 1 }}
              >
                {error}
              </Typography>
            )}
          </Box>

          {/* Preview */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview ({previewItems.filter((i) => i.newName !== i.originalName).length}{' '}
              changes)
            </Typography>
            <Box
              sx={{
                maxHeight: 250,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              <List dense>
                {previewItems.map((item, idx) => (
                  <ListItem
                    key={idx}
                    disableGutters
                    sx={{
                      py: 0.5,
                      opacity: item.newName !== item.originalName ? 1 : 0.6,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="caption"
                            sx={{
                              textDecoration:
                                item.newName !== item.originalName
                                  ? 'line-through'
                                  : 'none',
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.originalName}
                          </Typography>
                          {item.newName !== item.originalName && (
                            <>
                              <Typography variant="caption">→</Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'success.main',
                                  fontWeight: 500,
                                  flex: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {item.newName}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>

          {/* Summary */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`Total: ${previewItems.length}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Changing: ${previewItems.filter((i) => i.newName !== i.originalName).length}`}
              size="small"
              color={
                previewItems.some((i) => i.newName !== i.originalName)
                  ? 'info'
                  : 'default'
              }
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          data-testid="batch-rename-cancel-button"
        >
          Cancel
        </Button>
        <Button
          data-testid="batch-rename-submit-button"
          onClick={handleRename}
          variant="contained"
          disabled={isProcessing || !hasChanges}
          sx={{ minWidth: '120px' }}
        >
          {isProcessing ? 'Renaming...' : 'Rename All'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
